import * as Blurhash from "blurhash"
import type * as BlurhashWorker from "./blurhash.worker"

export async function decode(blurhash: string, options: decode.Options) {
  const { width, height } = options

  return await new Promise<Uint8ClampedArray<ArrayBuffer>>(
    (resolve, reject) => {
      const id = crypto.randomUUID()
      pendingRequests.set(id, { resolve, reject })
      worker.current.postMessage({
        id,
        payload: {
          blurhash,
          width,
          height,
        },
      })
    },
  )
}

export declare namespace decode {
  type Options = {
    width: number
    height: number
  }
}

export function encode(data: Uint8ClampedArray, options: encode.Options) {
  const { componentX = 4, componentY = 4, height, width } = options
  return Blurhash.encode(data, width, height, componentX, componentY)
}

export declare namespace encode {
  type Options = {
    componentX?: number | undefined
    componentY?: number | undefined
    height: number
    width: number
  }
}

////////////////////////////////////////////////////////////
// Internal

const pendingRequests = new Map<
  string,
  {
    resolve: (pixels: Uint8ClampedArray<ArrayBuffer>) => void
    reject: (error: Error) => void
  }
>()

export const worker = {
  self: null as Worker | null,
  get current() {
    if (this.self) this.self

    this.self = new Worker(new URL("./blurhash.worker.ts", import.meta.url), {
      type: "module",
    })

    this.self.onmessage = (event: MessageEvent<BlurhashWorker.Response>) => {
      const { id, data, error } = event.data
      const request = pendingRequests.get(id)

      if (request) {
        pendingRequests.delete(id)
        if (error) request.reject(new Error(error))
        else request.resolve(data!)
      }
    }

    this.self.onerror = (error) => {
      for (const [id, request] of pendingRequests) {
        request.reject(new Error(error.message))
        pendingRequests.delete(id)
      }
    }

    return this.self
  },
}
